import {
  ThemeProvider,
  createThemeProvider,
} from 'src/game/theme/ThemeProvider';
import { rescueLevelThemeDrowning } from './themes/rescueLevel.theme.drowning';
import { View, ViewOpts } from '@play-co/timestep-core/ui';
import { ThemeAsset } from '../theme/Theme';
import { RescueLevelTheme } from 'src/replicant/rescueLevel/rescueLevel.ruleset';
import type { PetEntity } from '../logic/EntityManager';
import { rescueLevelThemeFire } from './themes/rescueLevel.theme.fire';
import { rescueLevelThemeCrocodile } from './themes/rescueLevel.theme.crocodile';
import { rescueLevelThemeFrozen } from './themes/rescueLevel.theme.frozen';

/**
 * Type of the header component used in Rescue Level themes
 */
export type RescueLevelHeader = View & {
  pet: PetEntity;
  tenSecsLeft: boolean;
  playIdle: () => Promise<void>;
  playPuzzleStart?: (timeUntilTenSecsMs: number) => void;
  playTenSecsLeft: () => Promise<void>;
  playRescue: () => Promise<void>;
  playFail: () => Promise<void>;
  playQuit: () => Promise<void>;
};

/**
 * Type of the theme provider for Rescue Level
 */
type RescueLevelThemeProvider = {
  getHeader: (opts: ViewOpts) => RescueLevelHeader;
  getDream: (opts: ViewOpts) => View;
  getIconLarge: (opts: ViewOpts) => View;
  getIconSmall: (opts: ViewOpts) => View;
  getAssets: () => ThemeAsset[];
};

/**
 * Available themes
 */
const themes: Record<RescueLevelTheme, RescueLevelThemeProvider> = {
  drowning: rescueLevelThemeDrowning,
  fire: rescueLevelThemeFire,
  crocodile: rescueLevelThemeCrocodile,
  frozen: rescueLevelThemeFrozen,
};

/**
 * Cached theme providers
 */
const providers: Partial<
  Record<
    RescueLevelTheme,
    ThemeProvider<RescueLevelTheme, RescueLevelThemeProvider>
  >
> = {};

/**
 * Current theme in use
 */
let currentTheme: RescueLevelTheme = 'drowning';

/**
 * Get the theme provider
 */
export function getRescueLevelTheme(): ThemeProvider<
  RescueLevelTheme,
  RescueLevelThemeProvider
> {
  if (!providers[currentTheme]) {
    providers[currentTheme] = createThemeProvider(themes, currentTheme);
  }
  return providers[currentTheme]!;
}

/**
 * Set the current theme provider
 */
export function setRescueLevelTheme(theme: RescueLevelTheme) {
  currentTheme = theme;
}

/**
 * Get the current theme name
 */
export function getRescueLevelThemeName() {
  return currentTheme;
}
